import type { FilterAmounts } from "@/shared/hooks/usePetPlans";
import { CoverageSettings, Quote } from "@/shared/types/Quote.interface";
import { DefaultPlanResponse } from "@/shared/utils/QuoteAPI";
import { PublicConfig } from "@/shared/PublicConfig";
import { DefaultPlanUtils, ValidAnnualLimitsSchema, ValidDeductiblesSchema, ValidReimbursementRatesSchema } from "@/shared/utils/DefaultPlanUtils";

/**
 * ENG-17824: CA compliance. These two tables are the single source of truth for
 * state-forbidden plan options. The filter (usePetPlans filterAmounts), the
 * new-quote default remap, and the saved-quote planId remap all read from here.
 */
export const STATE_PLAN_EXCLUSIONS: Record<string, FilterAmounts> = {
    CA: { deductible: [1000], annualLimit: [2500, 3000, 4000] }
};

type StateRemapTable = { deductible: Record<number, number>; annualLimit: Record<number, number> };

export const STATE_PLAN_REMAPS: Record<string, StateRemapTable> = {
    CA: {
        deductible: { 1000: 750 },
        annualLimit: { 2500: 5000, 3000: 5000, 4000: 5000 }
    }
};

export class PlanFilterUtils {
    static getStatePlanExclusions(state: string | undefined): FilterAmounts | undefined {
        if (!state) return undefined;
        return STATE_PLAN_EXCLUSIONS[state.toUpperCase()];
    }

    static mergeFilterAmounts(...filters: (FilterAmounts | undefined)[]): FilterAmounts | undefined {
        const merged: FilterAmounts = {};
        for (const filter of filters) {
            if (!filter) continue;
            for (const key of ["annualLimit", "deductible", "reimbursementRate"] as const) {
                const values = filter[key];
                if (values?.length) {
                    merged[key] = Array.from(new Set([...(merged[key] ?? []), ...values]));
                }
            }
        }
        return Object.keys(merged).length ? merged : undefined;
    }

    static remapCoverageSettingsForState<T extends CoverageSettings | null | undefined>(settings: T, state: string | undefined): T {
        const remap = state ? STATE_PLAN_REMAPS[state.toUpperCase()] : undefined;
        const amounts = settings?.amounts;
        if (!remap || !amounts) return settings;
        const annualDeductible = amounts.annualDeductible != null ? (remap.deductible[amounts.annualDeductible] ?? amounts.annualDeductible) : amounts.annualDeductible;
        const annualLimit = amounts.annualLimit != null ? (remap.annualLimit[amounts.annualLimit] ?? amounts.annualLimit) : amounts.annualLimit;
        if (annualDeductible === amounts.annualDeductible && annualLimit === amounts.annualLimit) return settings;

        const withDeductible = annualDeductible != null ? PlanFilterUtils.withDeductible(settings, annualDeductible) : settings;
        return { ...withDeductible, amounts: { ...withDeductible.amounts, annualDeductible, annualLimit } } as T;
    }

    /**
     * The same settings on another annual deductible. Coverage ids encode the deductible
     * (Level{n}~Deductible{d}~Copay{c}), so they are regenerated to match — otherwise the quote rates off
     * the old value. Wellness ids are rider ids and carry no deductible, so they are left alone. Returns
     * the input untouched when it already sits on `annualDeductible`.
     */
    static withDeductible<T extends CoverageSettings | null | undefined>(settings: T, annualDeductible: number): T {
        const amounts = settings?.amounts;
        if (!settings || !amounts || amounts.annualDeductible === annualDeductible) return settings;

        let coverages = settings.coverages;
        if (amounts.reimbursementRate != null && coverages?.length) {
            const level = coverages.some(coverage => coverage.type === "illness") ? 2 : 1;
            const regeneratedId = DefaultPlanUtils.generateStandardPlanId(level, annualDeductible, amounts.reimbursementRate);
            coverages = coverages.map(coverage => (coverage.type?.startsWith("wellness") ? coverage : { ...coverage, id: regeneratedId }));
        }
        return { ...settings, coverages, amounts: { ...amounts, annualDeductible } } as T;
    }

    static remapPoliciesForState(policies: Quote["policies"], state: string | undefined): Quote["policies"] {
        if (!policies?.length || !PlanFilterUtils.getStatePlanExclusions(state)) return policies;
        return policies.map(policy => ({
            ...policy,
            coverageSettings: PlanFilterUtils.remapCoverageSettingsForState(policy.coverageSettings, state)
        }));
    }

    static remapDefaultPlanForState(plan: DefaultPlanResponse | undefined, state: string | undefined): DefaultPlanResponse | undefined {
        const remap = state ? STATE_PLAN_REMAPS[state.toUpperCase()] : undefined;
        if (!plan || !remap) return plan;
        const limitValue = plan.annualLimit?.value;
        const deductibleValue = plan.deductible?.value;
        const newLimit = limitValue != null ? (remap.annualLimit[limitValue] ?? limitValue) : limitValue;
        const newDeductible = deductibleValue != null ? (remap.deductible[deductibleValue] ?? deductibleValue) : deductibleValue;
        if (newLimit === limitValue && newDeductible === deductibleValue) return plan;
        return {
            ...plan,
            annualLimit: plan.annualLimit && newLimit != null ? { ...plan.annualLimit, value: newLimit } : plan.annualLimit,
            deductible: plan.deductible && newDeductible != null ? { ...plan.deductible, value: newDeductible } : plan.deductible
        };
    }

    static hasForbiddenCoverage(settings: CoverageSettings | null | undefined, state: string | undefined): boolean {
        const exclusions = PlanFilterUtils.getStatePlanExclusions(state);
        const amounts = settings?.amounts;
        if (!exclusions || !amounts) return false;
        const deductibleForbidden = amounts.annualDeductible != null && !!exclusions.deductible?.includes(amounts.annualDeductible);
        const limitForbidden = amounts.annualLimit != null && !!exclusions.annualLimit?.includes(amounts.annualLimit);
        return deductibleForbidden || limitForbidden;
    }

    /**
     * Builds a FULLY-SPECIFIED planId (level + limit + deductible + reimbursement) from a
     * policy's current settings, substituting state-forbidden values via STATE_PLAN_REMAPS.
     * Full specification is mandatory: parsePlanId fills missing segments from the pcode
     * default plan, whose $2,500 limit is itself CA-forbidden.
     * Out-of-vocabulary stored values (e.g. legacy $300 deductibles) are clamped to the
     * nearest valid, non-forbidden vocabulary value. Returns null when a compliant planId
     * cannot be expressed — callers must then SKIP the remap, never fall through to defaults.
     */
    static buildCompliantPlanId(settings: CoverageSettings, state: string | undefined): string | null {
        const key = state?.toUpperCase() ?? "";
        const remap = STATE_PLAN_REMAPS[key];
        const exclusions = STATE_PLAN_EXCLUSIONS[key];
        const amounts = settings?.amounts;
        if (!remap || !exclusions || !amounts) return null;
        const { annualDeductible, annualLimit, reimbursementRate } = amounts;
        if (annualDeductible == null || annualLimit == null || reimbursementRate == null) return null;

        const level = settings.coverages?.some(coverage => coverage.type === "illness") ? 2 : 1;

        const validDeductibles = ValidDeductiblesSchema.options.map(option => option.value).filter(value => !exclusions.deductible?.includes(value));
        const validLimits = ValidAnnualLimitsSchema.options
            .map(option => option.value)
            .filter(value => value !== PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE && !exclusions.annualLimit?.includes(value));
        const validReimbursements = ValidReimbursementRatesSchema.options.map(option => option.value);

        const deductible = PlanFilterUtils.clampToNearest(remap.deductible[annualDeductible] ?? annualDeductible, validDeductibles);
        const reimbursement = PlanFilterUtils.clampToNearest(reimbursementRate, validReimbursements);

        let limitSegment: string | null = null;
        if (annualLimit === PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE) {
            limitSegment = "limitunlimited";
        } else {
            const limit = PlanFilterUtils.clampToNearest(remap.annualLimit[annualLimit] ?? annualLimit, validLimits);
            limitSegment = limit == null ? null : `limit${limit}`;
        }

        if (deductible == null || reimbursement == null || limitSegment == null) return null;
        return `level${level}-${limitSegment}-deductible${deductible}-reimbursement${reimbursement}`;
    }

    static clampToNearest(value: number, allowed: number[]): number | null {
        if (!allowed.length) return null;
        if (allowed.includes(value)) return value;
        return allowed.reduce((best, candidate) => (Math.abs(candidate - value) < Math.abs(best - value) ? candidate : best), allowed[0] as number);
    }
}
